import { defineStyle, defineStyleConfig } from '@chakra-ui/react';

export const baseStyle = defineStyle({
  fontWeight: 'bold',
  fontFamily: 'heading',
  borderRadius: 'full',
  textTransform: 'uppercase',
  textDecoration: 'none',
  _focus: {
    outline: '4px solid',
    outlineColor: 'tmtBlue',
    outlineOffset: '4px',
    boxShadow: 'none',
    _dark: {
      outlineColor: 'tmtGold',
    },
  },
});

export const uflLarge = defineStyle({
  py: '0.875rem',
  px: 6,
  height: 'auto',
});

export const orange = defineStyle({
  ...uflLarge,
  background: 'orange.500',
  '& .subIcon': {
    background: 'orange.500',
  },
  color: 'headingFontColor',
  _hover: {
    background: 'orange.400',
    '& .subIcon': {
      background: 'orange.400',
    },
    _disabled: {
      background: 'gray.100',
      color: 'gray.600',
    },
  },
  _disabled: {
    background: 'gray.100',
    color: 'gray.600',
    opacity: 1,
  },
});

export const blue = defineStyle({
  ...uflLarge,
  background: 'blue.500',
  '& .subIcon': {
    background: 'blue.500',
  },
  color: 'darkHeadingFontColor',
  _hover: {
    background: 'blue.600',
    '& .subIcon': {
      background: 'blue.600',
    },
    _disabled: {
      background: 'gray.100',
      color: 'gray.600',
    },
  },
  _disabled: {
    background: 'gray.100',
    color: 'gray.600',
    opacity: 1,
  },
});

export const primary = defineStyle({
  ...orange,
  _dark: {
    ...blue,
  },
});

export const stepSubmit = defineStyle({
  ...primary,
  ...uflLarge,
  minW: '50%',
});

export const secondary = defineStyle({
  ...blue,
  _dark: {
    ...orange,
  },
});

export const gray = defineStyle({
  background: 'gray.600',
  color: 'darkBodyFontColor',
  _hover: {
    background: 'gray.700',
  },
  // _focus: {
  //   outline: '4px solid',
  //   outlineColor: 'tmtBlue',
  // },
  _dark: {
    background: 'gray.500',
    color: 'black',
    _hover: {
      background: 'gray.400',
    },
    // _focus: {
    //   outlineColor: 'tmtGold',
    // },
  },
});

const destructive = defineStyle({
  background: 'errorColor',
  color: 'darkHeadingFontColor',
  _hover: {
    background: 'errorHoverColor',
  },
});

const linkBase = defineStyle({
  textDecoration: 'underline',
  fontFamily: 'body',
  fontWeight: 'normal',
  h: 'auto',
  lineHeight: 'inherit',
  p: 0,
  textTransform: 'none',
  verticalAlign: 'inherit',
  _hover: {
    textDecoration: 'none',
  },
});

const link = defineStyle({
  ...linkBase,
  color: 'linkColor',
  _hover: {
    color: 'linkColorHover',
  },
  _dark: {
    color: 'darkLinkColor',
    _hover: {
      color: 'darkLinkColorHover',
    },
  },
});

const subtleLink = defineStyle({
  ...linkBase,
  color: 'bodyFontColor',
  _dark: {
    color: 'darkBodyFontColor',
  },
});

const footerLink = defineStyle({
  ...linkBase,
  color: 'tmtGold',
  _hover: {
    color: 'orange.400',
  },
  _focus: {
    outlineColor: 'tmtGold',
    boxShadow: 'none',
  },
});

const inlineLink = defineStyle({
  textDecoration: 'underline',
  color: 'black',
  textTransform: 'none',
  padding: 0,
  mt: '-2px',
  fontWeight: 'normal',
  fontFamily: 'body',
  borderRadius: 0,
  _hover: {
    textDecoration: 'none',
    color: 'black',
  },
  _focus: {
    outlineColor: 'tmtGold',
    boxShadow: 'none',
  },
});

const actionButton = defineStyle({
  borderRadius: 1,
});

const popoverButton = defineStyle({
  fontSize: 'xs',
  minW: 0,
  h: 'inherit',
  color: 'tmtBlue',
  _dark: {
    color: 'tmtGold',
  },
});

const menuItem = defineStyle({
  fontWeight: 'normal',
  fontFamily: 'body',
  textTransform: 'none',
  borderRadius: 'none',
  minW: 'auto',
  '&:not(:first-of-type)': {
    borderTop: '1px',
  },
  _hover: {
    bg: 'tmtBlue',
    color: 'white',
    zIndex: 2,
  },
  _focus: {
    bg: 'tmtBlue',
    color: 'white',
    zIndex: 2,
  },
  _dark: {
    _hover: {
      bg: 'tmtGold',
      color: 'black',
    },
    _focus: {
      bg: 'tmtGold',
      color: 'black',
    },
  },
});

const action = defineStyle({
  p: 2,
  fontSize: 'sm',
  fontWeight: 'normal',
  display: 'flex',
  flexDirection: 'column',
  gap: 0.5,
  h: 'full',
  borderRadius: 0,
  _hover: {
    textDecoration: 'underline',
    bg: 'tmtBlue',
    color: 'white',
  },
  _dark: {
    _hover: {
      bg: 'tmtGold',
      color: 'black',
    },
  },
});

const mobileMenuToggle = defineStyle({
  flex: '0 0 auto',
  height: 'auto',
  display: 'block',
  position: 'absolute',
  top: { base: '30px', md: 0 },
  right: 0,
  borderLeft: '1px',
  borderRadius: 0,
  px: { base: '20px', md: '25px' },
  py: { base: '14.5px', md: '18px' },
  fontWeight: 'light',
  fontSize: { base: 'sm', md: 'lg' },
  '.bar': {
    display: 'block',
    width: { base: '24px', md: '28px' },
    height: { base: '2px', md: '4px' },
    marginLeft: 'auto',
    marginRight: 'auto',
    bg: 'gray.800',
    position: 'relative',
    transition: 'all 0.2s ease',
    '+ .bar': {
      marginTop: 1,
    },
  },
  '&:hover, &:focus': {
    '.bar.first': {
      position: 'relative',
      top: { base: '6px', md: '9px' },
      left: '-6px',
      width: '20px',
      transform: 'rotate(45deg)',
    },
    '.bar.last': {
      position: 'relative',
      top: { base: '-6px', md: '-7px' },
      width: '20px',
      right: { base: '-7px', md: '-6px' },
      transform: 'rotate(-45deg)',
    },
    '.bar.middle': {
      opacity: 0,
    },
    '&[aria-expanded="true"]': {
      '.bar.first': {
        transform: 'rotate(-45deg)',
      },
      '.bar.last': {
        transform: 'rotate(45deg)',
      },
    },
  },
  _dark: {
    '.bar': {
      bg: 'white',
    },
  },
});

const addItem = defineStyle({
  borderRadius: '0',
  borderWidth: '1px',
  borderStyle: 'dashed',
  borderColor: 'borderColor',
  width: '100%',
  minH: { base: '60px', md: '75px' },
  _hover: {
    textDecoration: 'underline',
  },
  _dark: {
    borderColor: 'darkBorderColor',
  },
});

const iconButton = defineStyle({
  borderRadius: 0,
  _hover: {
    bg: 'gray.50',
  },
  '&[aria-expanded=true]': {
    bg: 'darkPrimaryButtonColor',
    color: 'darkPrimaryButtonTextColor',
    _hover: {
      bg: 'darkPrimaryButtonHoverColor',
    },
  },
  _dark: {
    _hover: {
      bg: 'gray.900',
    },
    '&[aria-expanded=true]': {
      bg: 'primaryButtonColor',
      color: 'primaryButtonTextColor',
      _hover: {
        bg: 'primaryButtonHoverColor',
      },
    },
  },
});

export const photoButton = defineStyle({
  bg: 'gray.50',
  border: '1px',
  borderRadius: '0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  p: 0,
  gap: { base: 3, md: 4 },
  height: 'auto',
  _hover: {
    borderColor: 'accentColor',
  },
  _dark: {
    bg: 'gray.800',
    _hover: {
      borderColor: 'primaryColor',
    },
  },
});

export const navButton = defineStyle({
  p: 0,
  w: '44px',
  h: '44px',
  bg: 'gray.50',
  color: 'gray.500',
  borderRadius: '0',
  _hover: {
    bg: 'darkPrimaryButtonHoverColor',
    color: 'darkPrimaryButtonTextColor',
  },
  _dark: {
    bg: 'gray.800',
    color: 'gray.400',
    _hover: {
      bg: 'primaryButtonHoverColor',
      color: 'primaryButtonTextColor',
    },
  },
});

export const modalCloseButton = defineStyle({
  position: 'absolute',
  top: -2,
  right: -2,
  m: 0,
  p: 0,
  zIndex: 1,
  color: 'white',
  borderRadius: '50%',
  w: 10,
  h: 10,
  '*': {
    display: 'block',
    lineHeight: 1,
  },
  _hover: {
    bg: 'primaryButtonColor',
    color: 'primaryButtonTextColor',
    '*': {
      color: 'primaryButtonTextColor',
    },
  },
  _focus: {
    outlineColor: 'primaryButtonColor',
    bg: 'primaryButtonColor',
    color: 'primaryButtonTextColor',
    '*': {
      color: 'primaryButtonTextColor',
    },
  },
});

export const orangeButton = defineStyle({
  ...orange,
});

export const blueButton = defineStyle({
  ...blue,
});

export const greenButton = defineStyle({
  ...uflLarge,
  background: 'green.500',
  '& .subIcon': {
    background: 'green.500',
  },
  color: 'headingFontColor',
  _hover: {
    background: 'green.400',
    '& .subIcon': {
      background: 'green.400',
    },
    _disabled: {
      background: 'green.500',
    },
  },
});

const dangerButton = defineStyle({
  ...uflLarge,
  background: 'errorLightColor',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'errorColor',
  color: 'tmtBlack',
  _hover: {
    background: '#FBCDDA',
  },
  _dark: {
    background: '#2F010E',
    color: '#fff',
    _hover: {
      background: '#470116',
      color: 'darkHeadingFontColor',
    },
  },
});

const shortcutButton = defineStyle({
  fontSize: 'sm',
  fontFamily: 'body',
  fontWeight: 'bold',
  flexShrink: 2,
  h: 'auto',
  p: 0,
  position: 'static',
  textAlign: 'left',
  textTransform: 'none',
  whiteSpace: 'pre-line',
  _before: {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  _hover: {
    textDecoration: 'underline',
  },
});

const dottedOutlineButton = defineStyle({
  ...uflLarge,
  borderStyle: 'dashed',
  borderWidth: '1px',
  color: 'headingFontColor',
  _dark: {
    color: 'darkHeadingFontColor',
  },
  _hover: {
    background: 'darkPrimaryButtonHoverColor',
    borderColor: 'darkPrimaryButtonHoverColor',
    color: 'darkPrimaryButtonTextColor',
    _dark: {
      background: 'primaryButtonHoverColor',
      borderColor: 'primaryButtonHoverColor',
      color: 'primaryButtonTextColor',
    },
  },
});

export default defineStyleConfig({
  defaultProps: {
    variant: 'primary',
  },
  baseStyle,
  sizes: {
    uflLarge,
  },
  variants: {
    primary,
    secondary,
    stepSubmit,
    destructive,
    link,
    subtleLink,
    actionButton,
    popoverButton,
    menuItem,
    action,
    footerLink,
    orange,
    blue,
    gray,
    mobileMenuToggle,
    inlineLink,
    addItem,
    iconButton,
    photoButton,
    modalCloseButton,
    navButton,
    orangeButton,
    blueButton,
    greenButton,
    dangerButton,
    shortcutButton,
    dottedOutlineButton,
  },
});
